import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import KeepLink from './KeepLink';
import ThemeChips from './ThemeChips';
import DetailsSection from './DetailsSection';
import { formatSource } from '../data/format';
import type { KuralFull, PaalEntry, ThemeEntry } from '../data/types';
import type { Lang } from '../hooks/useLang';
import './ReadingPanel.css';

interface Props {
    kural: KuralFull;
    themes: ThemeEntry[];
    paals: PaalEntry[];
    adhigaramFirstKuralN: number;
    lang: Lang;
}

export default function ReadingPanel({
    kural,
    themes,
    paals,
    adhigaramFirstKuralN,
    lang,
}: Props) {
    const paal = paals.find((p) => p.slug === kural.paal);
    const prevN = kural.n > 1 ? kural.n - 1 : null;
    const nextN = kural.n < 1330 ? kural.n + 1 : null;

    const [copied, setCopied] = useState(false);
    const copyLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch {
            /* ignore — clipboard may be blocked */
        }
    }, []);

    const leadTamil = lang === 'ta';

    return (
        <article className="reading-panel" aria-labelledby={`kural-${kural.n}-heading`}>
            <header className="reading-panel__lineage">
                <span className="reading-panel__number">Kural {kural.n}</span>
                <span aria-hidden="true" className="reading-panel__dot">
                    ·
                </span>
                <KeepLink to={`/kural/${adhigaramFirstKuralN}`} className="reading-panel__adh">
                    {leadTamil ? (
                        <>
                            <span lang="ta">{kural.adhigaram_title_ta}</span>
                            <span className="reading-panel__adh-sub">
                                {kural.adhigaram_title_en}
                            </span>
                        </>
                    ) : (
                        <>
                            <span>{kural.adhigaram_title_en}</span>
                            <span lang="ta" className="reading-panel__adh-sub">
                                {kural.adhigaram_title_ta}
                            </span>
                        </>
                    )}
                </KeepLink>
                <span aria-hidden="true" className="reading-panel__dot">
                    ·
                </span>
                <span className="reading-panel__meta">
                    {leadTamil ? (
                        <span lang="ta">{kural.iyal}</span>
                    ) : (
                        <span>{kural.iyal_en}</span>
                    )}
                </span>
                <span aria-hidden="true" className="reading-panel__dot">
                    ·
                </span>
                <span className="reading-panel__meta">
                    {paal
                        ? leadTamil
                            ? paal.title_ta
                            : paal.title_en
                        : kural.paal}
                </span>
            </header>

            <h1 id={`kural-${kural.n}-heading`} className="sr-only">
                Kural {kural.n}
            </h1>

            <VerseBlock lines={kural.verse_ta} />

            <div className="reading-panel__transliteration" aria-label="Transliteration">
                {kural.transliteration.map((line, i) => (
                    <div key={i}>{line}</div>
                ))}
            </div>

            {kural.tags.length > 0 && (
                <div className="reading-panel__themes">
                    <ThemeChips tags={kural.tags} themes={themes} lang={lang} />
                </div>
            )}

            <div className="reading-panel__sections">
                <DetailsSection
                    title="Translations"
                    count={kural.translations.length}
                    defaultOpen
                >
                    <SourceList entries={kural.translations} />
                </DetailsSection>

                {kural.commentary.length > 0 && (
                    <DetailsSection title="Commentary" count={kural.commentary.length}>
                        <SourceList entries={kural.commentary} />
                    </DetailsSection>
                )}

                {kural.paraphrases.length > 0 && (
                    <DetailsSection
                        title="Tamil Commentary"
                        count={kural.paraphrases.length}
                    >
                        <SourceList entries={kural.paraphrases} textLang="ta" />
                    </DetailsSection>
                )}
            </div>

            <nav className="reading-panel__pager" aria-label="Kural navigation">
                {prevN != null ? (
                    <KeepLink
                        to={`/kural/${prevN}`}
                        className="reading-panel__pager-btn reading-panel__pager-btn--prev"
                        aria-label={`Previous kural, ${prevN}`}
                    >
                        <span aria-hidden="true">←</span>
                        <span>Kural {prevN}</span>
                    </KeepLink>
                ) : (
                    <span
                        className="reading-panel__pager-btn reading-panel__pager-btn--disabled"
                        aria-disabled="true"
                    >
                        <span aria-hidden="true">←</span>
                        <span>Kural 1</span>
                    </span>
                )}
                <button
                    type="button"
                    onClick={copyLink}
                    className="reading-panel__copy"
                    aria-live="polite"
                >
                    {copied ? 'Copied' : 'Copy link'}
                </button>
                {nextN != null ? (
                    <KeepLink
                        to={`/kural/${nextN}`}
                        className="reading-panel__pager-btn reading-panel__pager-btn--next"
                        aria-label={`Next kural, ${nextN}`}
                    >
                        <span>Kural {nextN}</span>
                        <span aria-hidden="true">→</span>
                    </KeepLink>
                ) : (
                    <span
                        className="reading-panel__pager-btn reading-panel__pager-btn--disabled"
                        aria-disabled="true"
                    >
                        <span>Kural 1330</span>
                        <span aria-hidden="true">→</span>
                    </span>
                )}
            </nav>
        </article>
    );
}

/**
 * The Thirukkural couplet is metrically 4 words on line 1 and 3 words
 * on line 2. The couplet MUST render as exactly two visual lines
 * matching the source. We start each line at the CSS ``--type-verse``
 * size and, if either line would wrap in the available width, scale
 * both lines down uniformly so the whole couplet fits.
 *
 * We measure imperatively and set a CSS variable on the container to
 * avoid a re-render round-trip per resize. Re-measures on:
 *   • initial layout
 *   • container-size change (ResizeObserver)
 *   • font load complete (fonts.ready — the Tamil face swaps in
 *     after first paint, and the fallback face measures wider)
 */
function VerseBlock({ lines }: { lines: string[] }) {
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let rafId = 0;
        const scheduleMeasure = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                // Reset scale so we measure the natural width at max font.
                container.style.setProperty('--verse-scale', '1');
                const lineEls = container.querySelectorAll<HTMLElement>(
                    '[data-verse-line]'
                );
                if (lineEls.length === 0) return;
                const available = container.clientWidth;
                if (available === 0) return;
                let widest = 0;
                for (const el of lineEls) {
                    if (el.scrollWidth > widest) widest = el.scrollWidth;
                }
                if (widest === 0) return;
                const scale = Math.min(1, (available / widest) * 0.98);
                container.style.setProperty('--verse-scale', String(scale));
            });
        };

        scheduleMeasure();
        const ro = new ResizeObserver(scheduleMeasure);
        ro.observe(container);
        // Re-measure once fonts finish loading (the Tamil face may swap
        // in after our initial pass, invalidating the measurement).
        if (typeof document !== 'undefined' && 'fonts' in document) {
            document.fonts.ready.then(scheduleMeasure).catch(() => {
                /* ignore */
            });
        }
        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, [lines.join('\u0001')]);

    return (
        <div ref={containerRef} className="reading-panel__verse" lang="ta">
            {lines.map((line, i) => (
                <div
                    key={i}
                    data-verse-line=""
                    className="reading-panel__verse-line"
                >
                    {line}
                </div>
            ))}
        </div>
    );
}

function SourceList({
    entries,
    textLang,
}: {
    entries: { source: string; text: string }[];
    textLang?: 'ta';
}) {
    return (
        <ul className="source-list">
            {entries.map((e, i) => (
                <li key={i} className="source-list__item">
                    <span className="source-list__source" aria-label="Source">
                        {formatSource(e.source)}
                    </span>
                    <span
                        className="source-list__text"
                        {...(textLang ? { lang: textLang } : {})}
                    >
                        {e.text}
                    </span>
                </li>
            ))}
        </ul>
    );
}
